import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  InputSignal,
  Signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TagComponent } from '@app/shared/components/tag/tag.component';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { DialogService } from '@app/shared/services/shared/dialog.service';
import { TagsDataService } from '@app/shared/services/tags-data.service';
import { DialogModeEnum } from '@app/shared/types/enums/dialog-mode.enum';
import { Project } from '@app/shared/types/models/project/project.model';
import { Tag, TagFormDialogData } from '@app/shared/types/models/tag/tag.model';
import {
  ProffeoUiTableAction,
  ProffeoUiTableActionEvent,
  ProffeoUiTableCellTemplateContext,
  ProffeoUiTableColumn,
  UiTableComponent
} from '@app/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, switchMap } from 'rxjs';

@Component({
  selector: 'proffeo-project-tags-list',
  imports: [TagComponent, TranslatePipe, UiTableComponent],
  templateUrl: './project-tags-list.component.html'
})
export class ProjectTagsListComponent {
  private readonly dialogService: DialogService = inject(DialogService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly tagsService: TagsDataService = inject(TagsDataService);
  private readonly activeProjectService: ActiveProjectService = inject(ActiveProjectService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly deviceService: DeviceService = inject(DeviceService);
  private readonly tagCellTemplate = viewChild<TemplateRef<ProffeoUiTableCellTemplateContext<Tag>>>('tagCell');

  protected readonly isMobile: Signal<boolean> = this.deviceService.isMobile;
  protected readonly columns = computed((): ProffeoUiTableColumn<Tag>[] => [
    {
      columnId: 'name',
      columnName: this.translateService.instant('project-tags.list-headers.tag'),
      dataType: 'object',
      accessor: tag => tag,
      sortFunction: () => 0,
      sortable: false,
      hideable: false,
      widthPx: this.isMobile() ? 240 : 180,
      minWidthPx: 120,
      template: this.tagCellTemplate()
    },
    ...(!this.isMobile()
      ? [
          {
            columnId: 'description',
            columnName: this.translateService.instant('project-tags.list-headers.description'),
            dataType: 'string' as const,
            accessor: (tag: Tag): string => tag.description ?? '-',
            sortable: false,
            hideable: false,
            widthPx: 520,
            minWidthPx: 180,
            align: 'center' as const
          }
        ]
      : [])
  ]);
  protected readonly rowActions = computed((): ProffeoUiTableAction<Tag>[] => [
    {
      id: 'edit',
      label: this.translateService.instant('project-tags.list-actions.edit'),
      icon: 'edit'
    },
    {
      id: 'delete',
      label: this.translateService.instant('project-tags.list-actions.delete'),
      icon: 'delete'
    }
  ]);

  protected selectedProject: Signal<Project> = this.activeProjectService.activeProject;

  public tags: InputSignal<Tag[]> = input.required();

  protected resolveRowId(tag: Tag): string {
    return tag.id;
  }

  protected onTableAction(event: ProffeoUiTableActionEvent<Tag>): void {
    if (event.actionId === 'edit') {
      this.openEditProjectTagDialog(event.row);
      return;
    }

    if (event.actionId === 'delete') {
      this.openProjectTagDeleteConfirmationDialog(event.row);
    }
  }

  protected openProjectTagDeleteConfirmationDialog(projectTag: Tag): void {
    this.dialogService
      .openConfirmationDialog({
        title: this.translateService.instant('project-tags.modals.delete-title'),
        desc: this.translateService.instant('project-tags.modals.delete-description', {
          tagName: projectTag.name
        })
      })
      .afterClosed()
      .pipe(
        filter(decision => !!decision),
        switchMap(() => this.tagsService.deleteTag(this.selectedProject()?.id, projectTag)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected openEditProjectTagDialog(tag: Tag): void {
    const data: TagFormDialogData = { projectTagToEdit: tag, mode: DialogModeEnum.EDIT };
    this.dialogService
      .openTagFormDialog(data)
      .afterClosed()
      .pipe(
        filter(result => !!result),
        switchMap(result => this.tagsService.updateTag(this.selectedProject()?.id, tag.id, result)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
